import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogSurface,
  DialogTitle,
  DialogContent,
  DialogBody,
  DialogActions,
  Button,
  Text,
  Spinner,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import { AuthProvider } from '@m365-core-design/sandbox';

interface LLMAuthDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onAuthSuccess: () => void;
  onAuthCancel: () => void;
}

const useStyles = makeStyles({
  dialog: {
    maxWidth: '400px',
    minWidth: '320px',
    padding: tokens.spacingHorizontalXXL, // 24px padding like Figma
  },
  
  dialogContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalS, // 8px gap like Figma
    width: '100%',
  },
  
  loadingContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: tokens.spacingHorizontalS,
    padding: tokens.spacingVerticalL,
  },
  
  authContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
  },
  
  description: {
    color: tokens.colorNeutralForeground1, // Use primary foreground color like Figma
    lineHeight: tokens.lineHeightBase300, // 20px line height
    fontSize: tokens.fontSizeBase300, // 14px font size
    fontFamily: tokens.fontFamilyBase,
    fontWeight: tokens.fontWeightRegular,
    width: '100%',
  },
  
  buttonGroup: {
    display: 'flex',
    gap: tokens.spacingHorizontalS, // 8px gap between buttons
    justifyContent: 'flex-end',
    paddingTop: tokens.spacingVerticalXS, // 4px top padding like Figma footer
    width: '100%',
  },
  
  signInButton: {
    minWidth: '96px', // Match Figma button min-width
    '& > span': {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '4px', // 4px spacing between spinner and label
      whiteSpace: 'nowrap', // Prevent text wrapping
    },
  },
  
  cancelButton: {
    minWidth: '96px', // Match Figma button min-width
  },
  
  errorText: {
    color: tokens.colorPaletteRedForeground1,
    fontSize: tokens.fontSizeBase200,
  },
  
  successText: {
    color: tokens.colorPaletteGreenForeground1,
    fontSize: tokens.fontSizeBase200,
  },
});

const LLMAuthDialog: React.FC<LLMAuthDialogProps> = ({
  isOpen,
  onClose,
  onAuthSuccess,
  onAuthCancel,
}) => {
  const styles = useStyles();
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isAuthenticating, setIsAuthenticating] = useState(false);
  const [authError, setAuthError] = useState<string>('');
  
  // Check authentication status when dialog opens
  useEffect(() => {
    if (isOpen) {
      checkAuthStatus();
    }
  }, [isOpen]);
  
  const checkAuthStatus = async () => {
    setIsLoading(true);
    setAuthError('');
    
    try {
      const isLoggedIn = await AuthProvider.shared.isLoggedIn();
      setIsAuthenticated(isLoggedIn);
      
      if (isLoggedIn) {
        // User is already authenticated, call success callback after a brief delay
        setTimeout(() => {
          onAuthSuccess();
        }, 500);
      }
    } catch (error) {
      console.error('Error checking auth status:', error);
      setAuthError('Failed to check authentication status');
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleAuthenticate = async () => {
    setIsAuthenticating(true);
    setAuthError('');
    
    try {
      console.log('Attempting to authenticate with Microsoft LLM service...');
      
      // Use the actual logIn method from AuthProvider
      await AuthProvider.shared.logIn();
      console.log('User is now logged in');
      
      // Verify authentication was successful
      const authResult = await AuthProvider.shared.isLoggedIn();
      
      if (authResult) {
        setIsAuthenticated(true);
        onAuthSuccess();
      } else {
        throw new Error('Authentication failed - login did not complete successfully');
      }
    } catch (error) {
      console.error('Authentication error:', error);
      setAuthError(error instanceof Error ? error.message : 'Authentication failed');
    } finally {
      setIsAuthenticating(false);
    }
  };
  
  const handleCancel = () => {
    setAuthError('');
    onAuthCancel();
    onClose();
  };
  
  const renderDialogContent = () => {
    if (isLoading) {
      return (
        <div className={styles.loadingContainer}>
          <Spinner size="small" />
          <Text>Checking authentication status...</Text>
        </div>
      );
    }
    
    if (isAuthenticated) {
      return (
        <div className={styles.authContainer}>
          <Text className={styles.successText}>
            ✓ Successfully authenticated with Microsoft LLM service
          </Text>
          <Text className={styles.description}>
            You can now use advanced AI features powered by Microsoft's language models.
          </Text>
        </div>
      );
    }
    
    return (
      <div className={styles.authContainer}>
        <Text className={styles.description}>
          To enable Microsoft LLM features, you need to authenticate with the Microsoft AI service. 
          This will allow you to access advanced language model capabilities in your documents.
        </Text>
        
        {authError && (
          <Text className={styles.errorText}>
            {authError}
          </Text>
        )}
      </div>
    );
  };
  
  const renderDialogActions = () => {
    if (isLoading) {
      return null;
    }
    
    if (isAuthenticated) {
      return (
        <div className={styles.buttonGroup}>
          <Button appearance="primary" onClick={onClose}>
            Continue
          </Button>
        </div>
      );
    }
    
    return (
      <div className={styles.buttonGroup}>
        <Button 
          appearance="secondary" 
          onClick={handleCancel}
          className={styles.cancelButton}
        >
          Cancel
        </Button>
        <Button 
          appearance="primary" 
          onClick={handleAuthenticate}
          disabled={isAuthenticating}
          className={styles.signInButton}
        >
          <span>
            {isAuthenticating ? (
              <>
                <Spinner size="tiny" />
                <span>Signing In...</span>
              </>
            ) : (
              'Sign In'
            )}
          </span>
        </Button>
      </div>
    );
  };
  
  return (
    <Dialog open={isOpen} onOpenChange={(_, data) => !data.open && onClose()}>
      <DialogSurface className={styles.dialog}>
        <DialogBody>
          <DialogTitle>Microsoft LLM Authentication</DialogTitle>
          <DialogContent className={styles.dialogContent}>
            {renderDialogContent()}
          </DialogContent>
          <DialogActions>
            {renderDialogActions()}
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
};

export default LLMAuthDialog;